import { DateField } from "@visitproof/types";
import { Timestamp } from "firebase/firestore";

/**
 * Converts a DateField (Timestamp, Date, or string) to a JavaScript Date.
 * Handles Firebase Timestamp, Date objects, and ISO date strings.
 */
export const toDate = (value: DateField | undefined | null): Date => {
  if (!value) {
    return new Date();
  }

  // If it's already a Date, return it
  if (value instanceof Date) {
    return value;
  }

  // If it's a string, parse it
  if (typeof value === "string") {
    return new Date(value);
  }

  // If it has a toDate method (Firebase Timestamp), call it
  if (typeof value === "object" && "toDate" in value && typeof value.toDate === "function") {
    return value.toDate();
  }

  // Fallback: try to construct a Date from it
  return new Date();
};

/**
 * Converts a DateField to a local date string (YYYY-MM-DD format).
 */
export const toLocalDateString = (value: DateField | undefined | null): string => {
  const date = toDate(value);
  return date.toLocaleDateString("en-CA"); // Returns YYYY-MM-DD format
};

/**
 * Converts a DateField to a local time string (HH:MM format).
 */
export const toLocalTimeString = (value: DateField | undefined | null): string => {
  const date = toDate(value);
  return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
};

// ============================================================================
// UTC Date Utilities
// These functions handle date-only fields (without time) using UTC to avoid
// timezone issues where dates appear shifted by a day.
// ============================================================================

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/**
 * Gets the ordinal suffix for a day number (1st, 2nd, 3rd, 4th, etc.)
 */
const getOrdinalSuffix = (day: number): string => {
  if (day >= 11 && day <= 13) return "th";
  switch (day % 10) {
    case 1: return "st";
    case 2: return "nd";
    case 3: return "rd";
    default: return "th";
  }
};

/**
 * Formats a DateField as a date string using UTC.
 * This avoids timezone issues where dates appear shifted by a day.
 *
 * @param value - The date value to format (Timestamp, Date, or string)
 * @param format - The format to use:
 *   - "short": "Jan 15, 2025"
 *   - "long": "January 15th, 2025"
 *   - "input": "2025-01-15" (for HTML date inputs)
 * @returns Formatted date string, or empty string if value is null/undefined
 *
 * @example
 * formatDateUTC(timestamp, "short")  // "Feb 16, 2025"
 * formatDateUTC(timestamp, "long")   // "February 16th, 2025"
 * formatDateUTC(timestamp, "input")  // "2025-02-16"
 */
export const formatDateUTC = (
  value: DateField | undefined | null,
  format: "short" | "long" | "input" = "short"
): string => {
  if (!value) return "";

  const date = toDate(value);
  if (isNaN(date.getTime())) return "";

  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();

  switch (format) {
    case "input":
      return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    case "long":
      return `${MONTHS_LONG[month]} ${day}${getOrdinalSuffix(day)}, ${year}`;
    case "short":
    default:
      return `${MONTHS_SHORT[month]} ${day}, ${year}`;
  }
};

/**
 * Returns today's date as a local YYYY-MM-DD string (suitable for HTML date
 * inputs). Uses the local calendar day rather than UTC, so it never shows
 * "tomorrow" or "yesterday" in western/eastern timezones.
 */
export const todayLocalDateString = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Parses a date string (YYYY-MM-DD) into a Firestore Timestamp at noon UTC.
 * Using noon UTC avoids timezone boundary issues where midnight UTC becomes
 * the previous day in western timezones.
 *
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Firestore Timestamp at noon UTC, or undefined if string is empty
 *
 * @example
 * parseDateToTimestamp("2025-02-16")  // Timestamp for Feb 16, 2025 12:00:00 UTC
 */
export const parseDateToTimestamp = (dateString: string): Timestamp | undefined => {
  if (!dateString) return undefined;
  const [year, month, day] = dateString.split("-").map(Number);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return undefined;
  const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  return Timestamp.fromDate(date);
};

/**
 * Calculates age from a date of birth using UTC to avoid timezone issues.
 *
 * @param dob - The date of birth (Timestamp, Date, or string)
 * @returns Age in years, or null if dob is null/undefined
 *
 * @example
 * calculateAgeUTC(childDob)  // 7
 */
export const calculateAgeUTC = (dob: DateField | undefined | null): number | null => {
  if (!dob) return null;

  const birthDate = toDate(dob);
  if (isNaN(birthDate.getTime())) return null;

  const today = new Date();
  const birthYear = birthDate.getUTCFullYear();
  const birthMonth = birthDate.getUTCMonth();
  const birthDay = birthDate.getUTCDate();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  const currentDay = today.getDate();

  let age = currentYear - birthYear;

  // Adjust if birthday hasn't occurred this year yet
  if (currentMonth < birthMonth || (currentMonth === birthMonth && currentDay < birthDay)) {
    age--;
  }

  return age;
};
